import React, { memo } from 'react';
import { LABEL_TYPES } from '../utils/labelConstants';
import { IA_CATEGORIES, detectCategory } from '../utils/iaCategories';
import { tsToDateStr } from '../utils/labelUtils';
import { GeoIcon, PencilIcon, TrashIcon } from '../utils/LabelIcons';
import useLabelSmartPosition from '../hooks/useLabelSmartPosition';

// ORIGINE DES DONNÉES (labelData)
// ─────────────────────────────────────────────────────────────────────────────
// 1. Au chargement du graphe, LabelEventGraphGeoDemo appelle fetchGraphData()
//    (labelEventGraphApi.js). La réponse contient un tableau `labels` (format
//    API snake_case) converti en camelCase par mapApiLabel() puis stocké dans
//    l'état `labels` du conteneur.
//
// 2. LabelEventGraph reçoit ce tableau via la prop configuration.labels.
//    Il génère des marqueurs Plotly invisibles (labelHoverTraces) positionnés
//    sur chaque label ; chaque marqueur embarque le timestamp `t` dans son
//    champ `customdata`.
//
// 3. Quand l'utilisateur survole un de ces marqueurs, le callback handleHover
//    de LabelEventGraph détecte que customdata est un timestamp (≠ object
//    phase) et appelle onLabelHover(pt.customdata, clientX, clientY).
//
// 4. Dans le conteneur, handleLabelHover recherche dans `labels` l'objet dont
//    `l.t === t`, puis appelle setHoveredLabel({ label: found, x, y }).
//
// 5. Le conteneur affiche <LabelHoverCard labelData={hoveredLabel.label} …/>
//    → c'est l'objet { id, t, labelType, iconType, label, note } issu de
//    l'état local, lui-même synchronisé avec l'API.

const LabelHoverCard = memo(
  ({ labelData, x, y, onEdit, onRemove, onMouseEnter, onMouseLeave }) => {
    const labelConfig = LABEL_TYPES[labelData.labelType] || LABEL_TYPES.IA;
    const catKey = labelData.category ?? (
      labelData.labelType !== 'IA' ? detectCategory(labelData.label) : null
    );
    const catEntry = catKey ? IA_CATEGORIES[catKey] : null;
    const accentColor = catEntry?.color ?? labelConfig.color;
    const emoji = catEntry?.emoji ?? null;
    const { ref, pos } = useLabelSmartPosition(x, y);

    const soundUrl = labelData.urlSon
      ? /^https?:\/\//i.test(labelData.urlSon)
        ? labelData.urlSon
        : `https://argos-v25.itmsol.fr/${labelData.urlSon}`
      : null;

    const handleSoundClick = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!soundUrl) return;
      const filename = labelData.urlSon.split('/').pop();
      try {
        const resp = await fetch(soundUrl);
        if (!resp.ok) throw new Error('fetch failed');
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      } catch {
        window.open(soundUrl, '_blank');
      }
    };

    return (
      <div
        ref={ref}
        className="leg-hover-anchor"
        style={{ left: pos.left, top: pos.top, visibility: pos.visibility }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="leg-hover-card" style={{ borderColor: accentColor }}>
          {/* Ligne 1 : icône + badge IA/Manuel + boutons */}
          <div className="leg-card-header">
            <div className="leg-card-type">
              <GeoIcon iconType={labelData.iconType} color={accentColor} size={13} />
              <span
                className="leg-source-badge"
                style={{ background: labelData.labelType === 'IA' ? '#0077b6' : '#e07b00' }}
              >
                {labelData.labelType === 'IA' ? 'IA' : 'Manuel'}
              </span>
            </div>
            {labelData.labelType !== 'IA' && (
              <div className="leg-card-btn-group">
                <button className="leg-btn-edit" onClick={onEdit} title="Éditer">
                  <PencilIcon size={14} />
                </button>
                <button className="leg-btn-trash" onClick={onRemove} title="Supprimer">
                  <TrashIcon size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Titre : emoji (si catégorie détectée) + nom du label */}
          <div className="leg-card-title" style={{ color: accentColor }}>
            {emoji && <span className="leg-card-title-emoji">{emoji}</span>}
            {labelData.label || labelConfig.label}
          </div>

          <div className="leg-divider" style={{ borderColor: accentColor + '44' }} />

          {/* Date */}
          <div className="leg-card-date">{tsToDateStr(labelData.t)}</div>

          {/* Note */}
          {labelData.note && (
            <div className="leg-card-note">{labelData.note}</div>
          )}

          {/* Précision */}
          {labelData.labelType === 'IA' && labelData.precision != null && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Score</span>
              <span className="leg-card-value">{labelData.precision} %</span>
            </div>
          )}

          {/* Son */}
          {labelData.labelType === 'IA' && soundUrl && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Son</span>
              <a href={soundUrl} className="leg-sound-link" onClick={handleSoundClick}>
                {labelData.urlSon.split('/').pop()}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default LabelHoverCard;
